import TransactionInterface from "@homegame/common/dist/interface/transaction.interface";
import TransactionType from "@homegame/common/dist/enum/transaction-type.enum";
import TransactionStatus from "@homegame/common/dist/enum/transaction-status.enum";
import dayjs from "dayjs";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import PaymentStatus from "./payment-status.component";
import formatCurrency from "../../core/helpers/format-currency.helper";
import TableInterface from "@homegame/common/dist/interface/table.interface";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import TransactionButtons from "./transaction-buttons.component";
import Divider from "@mui/material/Divider";
import { getTransactionText } from "../../core/helpers/get-transaction-text.helper";
import IconButton from "@mui/material/IconButton";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import { useMemo, useState } from "react";
import UserDebtsService from "../../core/service/user-debts.service";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const useStyles = makeStyles(() =>
  createStyles({
    wrap: {
      background:
        "linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))",
      backdropFilter: "blur(12px)",
      borderRadius: 12,
      border: "1px solid rgba(255,255,255,0.1)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      padding: 10,
      paddingBottom: 15,
      marginBottom: 10,
    },
    title: {
      color: "#fff",
      fontSize: 15,
      fontWeight: 400,
      textShadow: "none",
    },
    tableName: {
      width: "100%",
      fontSize: 12,
      color: "#fff",
      textShadow: "none",
      fontWeight: 300,
      "& svg": {
        fontSize: 14,
        marginBottom: -3,
        color: "#fff",
      },
    },
    value: {
      width: "100%",
      fontSize: 13,
      color: "#fff",
      textShadow: "none",
      fontWeight: 400,
      "& svg": {
        fontSize: 14,
        marginBottom: -3,
      },
    },
    date: {
      width: "100%",
      fontSize: 12,
      color: "#fff",
      textShadow: "none",
      fontWeight: 400,
      "& svg": {
        fontSize: 14,
        marginBottom: -3,
      },
    },
  }),
);

export function CashoutDebtItem({
  transaction,
  onEditClick,
  onDeleteClick,
  showControls = true,
  relatedTransactions,
}: {
  transaction: TransactionInterface | Partial<TransactionInterface>;
  onEditClick?: (
    transaction: TransactionInterface | Partial<TransactionInterface>,
  ) => void;
  onDeleteClick?: (
    transaction: TransactionInterface | Partial<TransactionInterface>,
  ) => void;
  showControls?: boolean;
  relatedTransactions?: (
    | TransactionInterface
    | Partial<TransactionInterface>
  )[];
}) {
  const debtsService = new UserDebtsService();
  const [assignDisabled, setAssignDisabled] = useState(false);
  const reassignTransaction = async () => {
    setAssignDisabled(true);
    try {
      await debtsService.reAssignDebt(transaction.id!);
    } finally {
      setAssignDisabled(false);
    }
  };

  const handleDelete = () => {
    onDeleteClick && onDeleteClick(transaction);
  };

  const handleEdit = () => {
    onEditClick && onEditClick(transaction);
  };

  console.log("checking reassign candidates", {
    current: transaction,
    relatedTransactions,
    matchList: relatedTransactions?.map((t) => ({
      idMatch: t.id !== transaction.id,
      typeMatch: t.type === transaction.type,
      statusMatch: t.status === TransactionStatus.PENDING,
      amountMatch: Number(t.amount) === Number(transaction.amount),
      toUserMatch: t.toUser?.id === transaction.toUser?.id,
      fromUserMatch: t.fromUser?.id === transaction.fromUser?.id,
      id: t.id,
    })),
  });

  const isFirstDeclinedWithMatchingPending = useMemo(() => {
    if (transaction.status !== TransactionStatus.DECLINED) return false;

    const sameGroupDeclined = relatedTransactions
      ?.filter(
        (t) =>
          t.status === TransactionStatus.DECLINED &&
          t.amount === transaction.amount &&
          t.fromUserId === transaction.fromUserId &&
          t.toUserId === transaction.toUserId,
      )
      .sort((a, b) =>
        dayjs(a.createdAt).isAfter(dayjs(b.createdAt)) ? 1 : -1,
      );

    const hasMatchingPending = relatedTransactions?.some(
      (t) =>
        t.status === TransactionStatus.PENDING &&
        t.amount === transaction.amount &&
        t.fromUserId === transaction.fromUserId &&
        t.toUserId === transaction.toUserId,
    );

    return hasMatchingPending && sameGroupDeclined?.[0]?.id === transaction.id;
  }, [transaction, relatedTransactions]);

  return (
    <Grid container className={useStyles().wrap}>
      <Grid item xs={12}>
        <Grid container>
          <Grid
            item
            xs={6}
            justifyContent="end"
            flexDirection="column"
            display="flex"
          >
            <Typography className={useStyles().value}>
              Debt to {transaction.toUser?.name}
            </Typography>
            <span>
              <PaymentStatus status={transaction.status} showIcons />
              {transaction.status === TransactionStatus.DECLINED &&
                isFirstDeclinedWithMatchingPending && (
                  <IconButton
                    disabled={assignDisabled}
                    onClick={reassignTransaction}
                    aria-label="re-assign"
                    size="small"
                  >
                    <AutorenewIcon fontSize="inherit" />
                  </IconButton>
                )}
            </span>
          </Grid>
          <Grid item xs={2} justifyContent="start" display="flex">
            <Typography
              className={useStyles().value}
              style={{ textAlign: "right", paddingTop: 15 }}
            >
              {formatCurrency(transaction.amount!, transaction.currency!)}
            </Typography>
          </Grid>
          <Grid item xs={4} justifyContent="end" display="flex">
            {showControls &&
              ![
                TransactionStatus.DECLINED,
                TransactionStatus.APPROVED,
                TransactionStatus.PENDING,
              ].includes(transaction.status!) && (
                <>
                  <IconButton
                    aria-label="edit"
                    color="primary"
                    onClick={handleEdit}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="delete"
                    color="secondary"
                    onClick={handleDelete}
                  >
                    <DeleteIcon color="error" fontSize="small" />
                  </IconButton>
                </>
              )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}

const getTableName = (table?: TableInterface) => {
  if (!table) return "";
  if (!table.deletedAt) return table.name;
  return (
    <>
      {`${table.name}`}
      <span style={{ color: "red" }}> (archived)</span>
    </>
  );
};

export default function TransactionItem({
  transaction,
  userId,
  showTableName,
  showStatus,
  onActionDone,
}: {
  transaction: TransactionInterface;
  userId?: number;
  showTableName?: boolean;
  showStatus?: boolean;
  onActionDone: () => any;
}) {
  const classes = useStyles({});

  const getTransactionNameText = (transaction: TransactionInterface) => {
    return getTransactionText(transaction, userId);
  };

  const chipsRealAmount = transaction.chips * transaction.rate;

  const transactionStatusDate = () => {
    if (transaction.type !== TransactionType.PAYMENT) return <></>;

    if (
      transaction.status === TransactionStatus.APPROVED &&
      transaction.approvedAt
    )
      return (
        <span style={{ display: "inline-block" }}>
          <span style={{ fontWeight: 500 }}>Approved:</span>{" "}
          {dayjs(transaction.approvedAt).format("MMMM D, YYYY h:mm A")}
        </span>
      );
    if (
      transaction.status === TransactionStatus.DECLINED &&
      transaction.declinedAt
    )
      return (
        <span>
          <span style={{ fontWeight: 500 }}>Declined:</span>{" "}
          {dayjs(transaction.declinedAt).format("MMMM D, YYYY h:mm A")}
        </span>
      );
    return <></>;
  };

  return (
    <>
      <Grid container className={classes.wrap}>
        <Grid item xs={8}>
          <Typography className={classes.title}>
            {getTransactionNameText(transaction)}
          </Typography>

          {showTableName && (
            <Typography className={classes.tableName}>
              In {getTableName(transaction.table)}
            </Typography>
          )}
          {/*<Typography className={classes.date} style={{ width: "100%" }}>*/}
          {/*  {dayjs(transaction.createdAt).format("MMMM D, YYYY h:mm A")}*/}
          {/*</Typography>*/}
        </Grid>

        <Grid item xs={4} justifyContent="end" display="flex">
          <PaymentStatus status={transaction.status} showIcons />
        </Grid>
        {transaction.status !== TransactionStatus.PENDING && (
          <Grid item mb={0.5} mt={1} xs={12}>
            <Divider style={{ borderColor: "rgba(238,238,238, 0.2)" }} />
          </Grid>
        )}
        <Grid item xs={8} justifyContent="space-between" display="flex"></Grid>

        <Grid item xs={4} justifyContent="end">
          {![TransactionType.PAYMENT, TransactionType.GAME_OFFSET].includes(
            transaction.type,
          ) && (
            <Typography
              className={classes.value}
              style={{
                color: "#fff",
                textAlign: "right",
              }}
            >
              {Math.abs(transaction.chips)} chips{" "}
              <small>
                ({formatCurrency(chipsRealAmount, transaction.currency)})
              </small>
            </Typography>
          )}
        </Grid>
        <Grid item xs={8} justifyContent="end" display="flex">
          <Typography className={classes.value}>
            {transactionStatusDate()}
          </Typography>
        </Grid>

        <Grid item xs={4} justifyContent="end">
          <Typography
            className={classes.value}
            style={{
              textAlign: "right",
              color:
                (transaction.amount === 0 ||
                  chipsRealAmount > transaction.amount) &&
                transaction.type === TransactionType.BUY_IN
                  ? "#d95e66"
                  : "#fff",
            }}
          >
            Money paid{" "}
            {formatCurrency(transaction.amount, transaction.currency)}
          </Typography>
        </Grid>
        <Grid item xs={4} justifyContent="end" display="flex"></Grid>

        {transaction.type === TransactionType.CASH_OUT &&
          transaction.children &&
          transaction.children.length > 0 && (
            <>
              <Grid item mb={0.5} mt={1} xs={12}>
                <Divider style={{ borderColor: "rgba(238,238,238, 0.2)" }} />
              </Grid>
              <Grid item xs={12} mt={1}>
                {transaction.children.map((t) => (
                  <CashoutDebtItem transaction={t} showControls={false} />
                ))}
              </Grid>
            </>
          )}
        {showStatus &&
          transaction.status === TransactionStatus.PENDING &&
          !(
            transaction.type === TransactionType.CASH_OUT &&
            transaction.children &&
            transaction.children.length > 0
          ) && (
            <>
              <Grid item mb={0.5} mt={1} xs={12}>
                <Divider style={{ borderColor: "rgba(238,238,238, 0.2)" }} />
              </Grid>
              <Grid item xs={12} justifyContent="center" display="flex">
                {showStatus && (
                  <TransactionButtons
                    userId={userId}
                    transaction={transaction}
                    onActionDone={onActionDone}
                  />
                )}
              </Grid>
            </>
          )}
      </Grid>
    </>
  );
}
